import type { v4 } from '@sap/ux-specification-types';
import type { Table, FilterBar, Header } from '../controls';
import {
    pageLayoutInformation,
    childId,
    syncRule,
    validity,
    descriptionSrcURL,
    hide
} from '../../../common/decoration/decorators';
import { getSettingsPathForPage } from '../../application';

/**
 * Sync rule deleting all views if the chart view gets deleted.
 *
 * @param manifestSection - manifest section of the given view
 */
function deleteAllALPViews(manifestSection: object): void {
    if (manifestSection['paths']?.[0]?.primary || manifestSection['paths']?.[0]?.secondary) {
        Object.keys(manifestSection).forEach((key) => delete manifestSection[key]);
    }
}

/**
 * Sync rule for preventing the export of a property.
 */
function doNotExport(): void {
    return;
}

/**
 * Sync rule for preventing the import of a property.
 */
function doNotImport(): void {
    return;
}

/**
 * Sync class for import and export of V4 list report
 */
@pageLayoutInformation({
    id: 'sap.suite.ui.generic.template.ListReport.view.ListReport',
    pageId: 'sap.suite.ui.generic.template.ListReport'
})
export class ListReport implements v4.ListReportConfigV4 {
    @syncRule({
        manifest: {
            path: (pageName): string => getSettingsPathForPage(pageName) + '/views',
            import: doNotImport,
            export: doNotExport,
            delete: deleteAllALPViews
        }
    })
    chart?: v4.ALPChartView;

    /* Table
    Example Ids: EntitySet - Booking
    s4v4lrop::BookingList--fe::table::Booking::LineItem-innerTable for sap.m.Table
    s4v4lrop::BookingList--fe::table::Booking::LineItem for sap.ui.mdc.Table
    */
    table?: v4.ALPTableView | Table;

    @syncRule({
        manifest: {
            path: (pageName: string[]): string => getSettingsPathForPage(pageName)
        }
    })
    @validity({
        since: '1.84.0'
    })
    variantManagement?: v4.VariantManagementTypeListReport;

    @syncRule({
        manifest: {
            path: (pageName): string => getSettingsPathForPage(pageName) + '/views/paths/[]/0'
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/2a9df06673d34f72b238549d49da8bfb')
    defaultPath?: v4.DefaultPathType;

    @syncRule({
        manifest: {
            path: (pageName): string => getSettingsPathForPage(pageName)
        }
    })
    @hide(true)
    defaultTemplateAnnotationPath?: string;

    @childId('header')
    header?: Header;

    // Currently childIds are not used in case of OData V4 Example Id - "fe::FilterBar::Booking", where Booking is entitySet
    @childId('fe::FilterBar')
    filterBar?: FilterBar;
}
